import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Commune, ItinerairePoint } from '../types/Commune';
import './ItineraireMap.css';

// Fix pour les icônes Leaflet avec React
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface ItineraireMapProps {
  selectedCommune: Commune;
  itineraire: ItinerairePoint[];
}

const ItineraireMap: React.FC<ItineraireMapProps> = ({ selectedCommune, itineraire }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);

  // Création de la carte UNE SEULE FOIS
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    console.log('🔍 Initialisation de la carte...');
    
    // Coordonnées par défaut (centre de la France) si pas de commune sélectionnée
    const defaultLat = 46.603354;
    const defaultLng = 1.888334;
    
    const initialLat = selectedCommune.latitude || defaultLat;
    const initialLng = selectedCommune.longitude || defaultLng;

    const map = L.map(mapRef.current).setView([initialLat, initialLng], 8);

    // Ajouter la couche de tuiles OpenStreetMap
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors'
    }).addTo(map);

    mapInstanceRef.current = map;
    console.log('✅ Carte initialisée');

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []); // Tableau vide = une seule fois

  // Mise à jour des marqueurs et polylines à chaque changement d'itinéraire ou de commune
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !selectedCommune.latitude || !selectedCommune.longitude) {
      console.log('⚠️ Conditions non remplies pour afficher l\'itinéraire');
      return;
    }

    console.log('🔍 Mise à jour de l\'itinéraire avec', itineraire.length, 'étapes');
    console.log('🔍 Détail de l\'itinéraire:', itineraire.map(p => ({ nom: p.commune.nom, ordre: p.ordre, coords: [p.commune.latitude, p.commune.longitude] })));

    // Nettoyer les anciens marqueurs/polylines/cercle
    map.eachLayer((layer) => {
      if (layer instanceof L.Marker || layer instanceof L.Polyline || layer instanceof L.Circle) {
        map.removeLayer(layer);
      }
    });

    // Ajouter le cercle de 50km autour de la commune sélectionnée
    const radiusCircle = L.circle(
      [selectedCommune.latitude, selectedCommune.longitude],
      {
        color: 'blue',
        fillColor: '#3388ff',
        fillOpacity: 0.1,
        radius: 50000 // 50km en mètres
      }
    ).addTo(map);

    // Créer les points de l'itinéraire
    const routePoints: L.LatLng[] = [];
    let validPointsCount = 0;
    
    itineraire.forEach((point, index) => {
      const { commune, ordre } = point;
      
      console.log(`🔍 Étape ${index}:`, commune.nom, 'Coordonnées:', commune.latitude, commune.longitude);
      
      // Vérifier que la commune a des coordonnées
      if (!commune.latitude || !commune.longitude) {
        console.log(`⚠️ Coordonnées manquantes pour ${commune.nom}`);
        return;
      }
      
      validPointsCount++;
      
      // Ajouter le point à l'itinéraire
      const latLng = L.latLng(commune.latitude, commune.longitude);
      routePoints.push(latLng);
      
      // Créer le marqueur
      const isStart = ordre === 0;
      const isEnd = ordre === itineraire.length - 1;
      
      console.log(`🔍 Marqueur ${index}: ${commune.nom} - ordre: ${ordre}, isStart: ${isStart}, isEnd: ${isEnd}, total: ${itineraire.length}, coords: [${commune.latitude}, ${commune.longitude}]`);
      
      let iconColor = '#3388ff';
      let iconText = (ordre + 1).toString();
      
      if (isStart) {
        iconColor = '#28a745';
        iconText = 'D';
      } else if (isEnd) {
        iconColor = '#dc3545';
        iconText = 'A';
      }

      const customIcon = L.divIcon({
        className: 'custom-marker',
        html: `<div style="
          background-color: ${iconColor};
          color: white;
          border-radius: 50%;
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        ">${iconText}</div>`,
        iconSize: [30, 30],
        iconAnchor: [15, 15]
      });

      const marker = L.marker(latLng, { icon: customIcon })
        .addTo(map)
        .bindPopup(`
          <div class="marker-popup">
            <h4>${commune.nom}</h4>
            <p><strong>ID:</strong> ${commune.id}</p>
            <p><strong>Ville:</strong> ${commune.ville} (${commune.codePostal})</p>
            <p><strong>Population:</strong> ${commune.nombreHabitants.toLocaleString()} hab.</p>
            <p><strong>Département:</strong> ${commune.departement}</p>
            <p><strong>Statut:</strong> ${commune.statut}</p>
            <p><strong>Ordre:</strong> ${ordre + 1}</p>
            ${point.distance > 0 ? `<p><strong>Distance:</strong> ${point.distance.toFixed(1)} km</p>` : ''}
          </div>
        `);
      
      console.log(`✅ Marqueur ajouté pour ${commune.nom} à la position:`, latLng, 'couleur:', iconColor, 'texte:', iconText);
    });

    console.log(`📊 Total des points valides: ${validPointsCount}/${itineraire.length}`);

    // Dessiner l'itinéraire
    if (routePoints.length > 1) {
      L.polyline(routePoints, {
        color: '#ff6b35',
        weight: 4,
        opacity: 0.8,
        dashArray: '10, 5'
      }).addTo(map);
      
      console.log('✅ Ligne d\'itinéraire ajoutée avec', routePoints.length, 'points');

      // Ajouter des flèches pour indiquer la direction
      routePoints.forEach((point, index) => {
        if (index < routePoints.length - 1) {
          const nextPoint = routePoints[index + 1];
          const midLat = (point.lat + nextPoint.lat) / 2;
          const midLng = (point.lng + nextPoint.lng) / 2;
          
          L.marker([midLat, midLng], {
            icon: L.divIcon({
              className: 'arrow-marker',
              html: '<div style="color: #ff6b35; font-size: 20px;">→</div>',
              iconSize: [20, 20],
              iconAnchor: [10, 10]
            })
          }).addTo(map);
        }
      });
    } else {
      console.log('⚠️ Pas assez de points pour dessiner l\'itinéraire');
    }

    // Ajuster la vue pour inclure tous les éléments
    if (routePoints.length > 0) {
      const bounds = L.latLngBounds(routePoints);
      bounds.extend(radiusCircle.getBounds());
      map.fitBounds(bounds, { padding: [20, 20] });
      console.log('✅ Vue ajustée aux limites:', bounds);
    } else {
      console.log('⚠️ Aucun point valide pour ajuster la vue');
    }

  }, [itineraire, selectedCommune.latitude, selectedCommune.longitude]);

  // Si pas de coordonnées, afficher un message
  if (!selectedCommune.latitude || !selectedCommune.longitude) {
    return (
      <div className="itineraire-map">
        <h3>Carte de l'itinéraire</h3>
        <div className="map-container no-coordinates">
          <p>⚠️ Coordonnées non disponibles pour cette commune</p>
          <p>Les coordonnées sont estimées à partir du code postal {selectedCommune.codePostal}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="itineraire-map">
      <h3>Carte de l'itinéraire</h3>
      <div className="map-legend">
        <div className="legend-item">
          <div className="legend-color" style={{ backgroundColor: '#28a745' }}>D</div>
          <span>Départ</span>
        </div>
        <div className="legend-item">
          <div className="legend-color" style={{ backgroundColor: '#3388ff' }}>1</div>
          <span>Étapes</span>
        </div>
        <div className="legend-item">
          <div className="legend-color" style={{ backgroundColor: '#dc3545' }}>A</div>
          <span>Arrivée</span>
        </div>
        <div className="legend-item">
          <div className="legend-line" style={{ backgroundColor: '#ff6b35' }}></div>
          <span>Itinéraire</span>
        </div>
        <div className="legend-item">
          <div className="legend-circle" style={{ borderColor: '#3388ff' }}></div>
          <span>Rayon 50km</span>
        </div>
      </div>
      <div ref={mapRef} className="map-container" />
    </div>
  );
};

export default ItineraireMap; 